
import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { interval, Subscription } from 'rxjs';
import { takeWhile } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';

import { UserLoginService } from '../../userlogin.service';
import { LoaderService } from 'src/app/loader.service';

import {
  AuthenticationRequest,
  UserLoginAuthResponseDTO,
  UserLoginGenerateOtpRequestDTO,
  UserLoginGenerateOtpResponseDTO,
  UserLoginValidateOtpRequestDTO
} from '../../userlogin.interface';

@Component({
  selector: 'app-school-login',
  templateUrl: './school-login.component.html',
  styleUrls: ['./school-login.component.css']
})
export class SchoolLoginComponent implements OnInit, OnDestroy {

  loginForm: FormGroup;
  mobileForm: FormGroup;
  otpForm: FormGroup;

  isMobileLogin = false;
  isOtpSent = false;
  maskedMessage = '';
  showPassword = false;

  timerSeconds = 0;
  private timerSub: Subscription | null = null;
  readonly OTP_TIMEOUT_SECONDS = 60;

  loading = false;
  resendOtpDisabled = true;

  roles = [
    {
      value: 'admin',
      label: 'Administrator',
      icon: 'fas fa-user-shield'
    },
    {
      value: 'teacher',
      label: 'Teacher',
      icon: 'fas fa-chalkboard-teacher'
    },
    {
      value: 'student',
      label: 'Student / Parent',
      icon: 'fas fa-user-graduate'
    }
  ];

  selectedRole = 'teacher';

  constructor(
    private fb: FormBuilder,
    private userLoginService: UserLoginService,
    private router: Router,
    private loader: LoaderService,
    private toastr: ToastrService
  ) {
    this.loginForm = this.fb.group({
      username: ['', Validators.required],
      password: ['', Validators.required],
      role: ['teacher', Validators.required]
    });

    this.mobileForm = this.fb.group({
      mobileNo: [
        '',
        [
          Validators.required,
          Validators.pattern(/^[6-9]\d{9}$/)
        ]
      ]
    });

    this.otpForm = this.fb.group({
      otp1: ['', Validators.required],
      otp2: ['', Validators.required],
      otp3: ['', Validators.required],
      otp4: ['', Validators.required],
      otp5: ['', Validators.required],
      otp6: ['', Validators.required],
      otp: ['']
    });
  }

  ngOnInit(): void {
    if (this.userLoginService.isAuthenticated()) {
      this.router.navigate(['/']);
    }
  }

  ngOnDestroy(): void {
    this.clearTimer();
  }

  togglePassword(): void {
    this.showPassword = !this.showPassword;
  }

  onLogin(): void {
    this.loginForm.markAllAsTouched();

    if (this.loginForm.invalid) {
      return;
    }

    const userData: AuthenticationRequest = {
      userName: this.loginForm.value.username,
      password: this.loginForm.value.password
    };

    this.userLoginService.UserLogin_Auth(userData).subscribe({
      next: (res: UserLoginAuthResponseDTO) => {
        const token = res.data?.token?.trim();

        if (token) {
          localStorage.setItem('access_token', token);

          localStorage.setItem(
            'user_role',
            this.loginForm.value.role
          );

          if (res.statusCode === 403) {
            this.router.navigate([
              '/reset-password',
              res.data.userId
            ]);
          } else {
            this.router.navigate(['/']);
          }
        } else {
          this.toastr.error(
            'Login response did not contain a token.'
          );
        }
      },

      error: () => {
        this.toastr.error(
          'Login failed. Please check your credentials.'
        );
      }
    });
  }

  sendOtp(): void {
    this.mobileForm.markAllAsTouched();

    if (this.mobileForm.invalid) {
      return;
    }

    this.loading = true;

    const payLoad: UserLoginGenerateOtpRequestDTO = {
      mobileNo: this.mobileForm.value.mobileNo
    };

    this.userLoginService
      .UserLogin_LoginGenerateOtp(payLoad)
      .subscribe({
        next: (res: UserLoginGenerateOtpResponseDTO) => {
          this.loading = false;

          if (res.statusCode === 200) {
            this.isOtpSent = true;
            this.maskedMessage = res.message;

            this.startTimerFromExpiry(res.expiryTime);

            setTimeout(() => {
              this.resendOtpDisabled = false;
            }, 90000);
          } else {
            this.toastr.warning(res.message);
          }
        },

        error: () => {
          this.loading = false;

          this.toastr.error(
            'Failed to send OTP. Please try again.'
          );
        }
      });
  }

  verifyOtp(): void {
    this.otpForm.markAllAsTouched();

    if (this.otpForm.invalid) {
      return;
    }

    const payLoad: UserLoginValidateOtpRequestDTO = {
      mobileNo: this.mobileForm.value.mobileNo,
      otp: this.otpForm.value.otp
    };

    this.loading = true;

    this.userLoginService
      .UserLogin_LoginValidateOtp(payLoad)
      .subscribe({
        next: (res) => {
          this.loading = false;

          if (res && res.statusCode === 200) {
            const token = res.data?.token;

            if (token) {
              localStorage.setItem(
                'access_token',
                token.trim()
              );

              this.router.navigate(['/']);
            } else {
              this.toastr.error(
                'OTP verification succeeded but no token received.'
              );
            }
          } else {
            this.toastr.error(
              'Invalid OTP. Please try again.'
            );
          }
        },

        error: () => {
          this.loading = false;

          this.toastr.error(
            'OTP verification failed.'
          );
        }
      });
  }

  startTimerFromExpiry(expiryTime: any): void {
    this.clearTimer();

    const expiry = new Date(expiryTime).getTime();
    const diff = Math.floor((expiry - Date.now()) / 1000);

    this.timerSeconds = diff > 0 ? diff : 0;

    if (this.timerSeconds <= 0) {
      return;
    }

    this.timerSub = interval(1000)
      .pipe(
        takeWhile(() => this.timerSeconds > 0)
      )
      .subscribe(() => {
        this.timerSeconds--;

        if (this.timerSeconds === 0) {
          this.clearTimer();
        }
      });
  }

  clearTimer(): void {
    if (this.timerSub) {
      this.timerSub.unsubscribe();
      this.timerSub = null;
    }

    this.timerSeconds = 0;
  }

  resendOtp(): void {
    this.sendOtp();
  }

  toggleLoginMode(): void {
    this.isMobileLogin = !this.isMobileLogin;

    this.loginForm.reset({
      role: 'teacher'
    });

    this.mobileForm.reset();
    this.otpForm.reset();

    this.isOtpSent = false;
    this.maskedMessage = '';
    this.resendOtpDisabled = true;

    this.clearTimer();
  }

  changeMobileNo(): void {
    this.isOtpSent = false;
    this.maskedMessage = '';

    this.otpForm.reset();
    this.clearTimer();
  }

  onOtpInput(event: any, index: number): void {
    const value = event.target.value;

    if (value.length === 1 && index < 6) {
      document
        .getElementById('otp' + (index + 1))
        ?.focus();
    }

    if (value.length === 0 && index > 1) {
      document
        .getElementById('otp' + (index - 1))
        ?.focus();
    }

    let finalOtp = '';

    for (let i = 1; i <= 6; i++) {
      finalOtp += this.otpForm.get('otp' + i)?.value || '';
    }

    this.otpForm.patchValue({
      otp: finalOtp
    });
  }
}
